import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import { Button } from "src/primitives/Button";
import { cn } from "src/shadcn/lib/utils";

import { buildMonth, isSameMonth } from "./utils";

interface MonthPickerProps {
  /** Currently selected date (used to highlight the active month). */
  value: Date | null;
  /** Year currently shown in the grid. */
  displayedYear: number;
  /** Callback when the user navigates to a different year. */
  onYearChange: (year: number) => void;
  /** Callback when a month is picked. */
  onSelect: (date: Date) => void;
  /** Predicate to disable an entire month (any day in it returning true disables the month). */
  isDisabled?: (date: Date) => boolean;
  /** Locale code for month-name formatting. */
  locale?: string;
}

const MonthPicker = ({
  value,
  displayedYear,
  onYearChange,
  onSelect,
  isDisabled,
  locale,
}: MonthPickerProps) => {
  const months = Array.from({ length: 12 }, (_, m) =>
    buildMonth(displayedYear, m)
  );
  const monthName = (date: Date) =>
    date.toLocaleString(locale, { month: "short" });

  return (
    <div className="bg-background flex w-[16rem] flex-col gap-3 p-2">
      <div className="flex items-center justify-between">
        <Button
          aria-label="Previous year"
          size="icon"
          variant="ghost"
          onClick={() => onYearChange(displayedYear - 1)}
        >
          <ChevronLeftIcon className="size-4 rtl:rotate-180" />
        </Button>
        <span className="text-sm font-medium">{displayedYear}</span>
        <Button
          aria-label="Next year"
          size="icon"
          variant="ghost"
          onClick={() => onYearChange(displayedYear + 1)}
        >
          <ChevronRightIcon className="size-4 rtl:rotate-180" />
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {months.map(monthDate => {
          const disabled = isDisabled?.(monthDate) ?? false;
          const selected = isSameMonth(value, monthDate);

          return (
            <button
              aria-pressed={selected}
              className={cn(
                "rounded-md py-2 text-sm transition-colors",
                "hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50",
                selected &&
                  "bg-primary text-primary-foreground hover:bg-primary"
              )}
              disabled={disabled}
              key={monthDate.getMonth()}
              type="button"
              onClick={() => onSelect(monthDate)}
            >
              {monthName(monthDate)}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export { MonthPicker };
